import { api } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sprint AJ — tiny module-level cache so DashboardLayout (SetupCompleteChip)
// and MyEvents (checklist + list) share a single /events/mine fetch instead
// of double-firing on the same route render.
let _mineCache = null; // { at: number, promise: Promise<events[]> }
const _MINE_TTL_MS = 8000;

export function invalidateMyEventsCache() {
  _mineCache = null;
}

export const eventsApi = {
  list: () => api.get("/events/mine").then((r) => r.data),
  listCached: () => {
    const now = Date.now();
    if (_mineCache && now - _mineCache.at < _MINE_TTL_MS) return _mineCache.promise;
    const promise = api
      .get("/events/mine")
      .then((r) => r.data)
      .catch((e) => {
        _mineCache = null;
        throw e;
      });
    _mineCache = { at: now, promise };
    return promise;
  },
  get: (id) => api.get(`/events/${id}`).then((r) => r.data),
  create: (payload) => api.post("/events", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/events/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/events/${id}`).then((r) => r.data),
  publish: (id) => api.post(`/events/${id}/publish`).then((r) => r.data),
  unpublish: (id) => api.post(`/events/${id}/unpublish`).then((r) => r.data),
  duplicate: (id) => api.post(`/events/${id}/duplicate`).then((r) => r.data),
  addTicketType: (eventId, payload) =>
    api.post(`/events/${eventId}/ticket-types`, payload).then((r) => r.data),
  updateTicketType: (eventId, ttId, payload) =>
    api.patch(`/events/${eventId}/ticket-types/${ttId}`, payload).then((r) => r.data),
  removeTicketType: (eventId, ttId) =>
    api.delete(`/events/${eventId}/ticket-types/${ttId}`).then((r) => r.data),
  // Admin all-organiser edit — archive/unarchive preserve historical orders while
  // hiding the ticket type from future sales. Delete stays hard-blocked when sold>0.
  archiveTicketType: (eventId, ttId, reason) =>
    api.post(`/events/${eventId}/ticket-types/${ttId}/archive`, { reason: reason || null }).then((r) => r.data),
  unarchiveTicketType: (eventId, ttId) =>
    api.post(`/events/${eventId}/ticket-types/${ttId}/unarchive`).then((r) => r.data),
  // Manual "close ticket sales" — event- and tier-level. body: {closed, label, message, reason}
  setEventSalesStatus: (eventId, body) =>
    api.post(`/events/${eventId}/sales-status`, body).then((r) => r.data),
  setTicketTypeSalesStatus: (eventId, ttId, body) =>
    api.post(`/events/${eventId}/ticket-types/${ttId}/sales-status`, body).then((r) => r.data),
  // Admin-only price override — bypasses "no price change after sales" lock; requires reason.
  adminUpdateTicketPrice: (eventId, ttId, priceCents, reason) =>
    api.patch(`/admin/events/${eventId}/ticket-types/${ttId}/price`, {
      price_cents: priceCents,
      reason: reason || null,
    }).then((r) => r.data),
  publicList: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ""),
    ).toString();
    return api.get(`/public/events${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  publicBySlug: (slug) => api.get(`/public/events/${slug}`).then((r) => r.data),
  publicByKey: (key) => api.get(`/public/events/private/${key}`).then((r) => r.data),
  publicRelated: (slug, limit = 3) =>
    api.get(`/public/events/${slug}/related?limit=${limit}`).then((r) => r.data),
  uploadCover: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    // Override the instance-level JSON default so axios emits the proper
    // multipart/form-data boundary (otherwise FastAPI 422s with "file required").
    return api
      .post("/uploads/event-cover", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
};

export const organisersApi = {
  publicBySlug: (slug) => api.get(`/public/organisers/${slug}`).then((r) => r.data),
};

export const venuesApi = {
  list: (includeArchived = false) =>
    api.get(`/venues${includeArchived ? "?include_archived=true" : ""}`).then((r) => r.data),
  get: (id) => api.get(`/venues/${id}`).then((r) => r.data),
  create: (payload) => api.post("/venues", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/venues/${id}`, payload).then((r) => r.data),
  archive: (id) => api.post(`/venues/${id}/archive`).then((r) => r.data),
  // Templates
  listTemplates: (venueId, includeArchived = false) =>
    api.get(`/venues/${venueId}/templates${includeArchived ? "?include_archived=true" : ""}`).then((r) => r.data),
  createTemplate: (venueId, payload) => api.post(`/venues/${venueId}/templates`, payload).then((r) => r.data),
  getTemplate: (id) => api.get(`/templates/${id}`).then((r) => r.data),
  updateTemplate: (id, payload) => api.patch(`/templates/${id}`, payload).then((r) => r.data),
  publishTemplate: (id) => api.post(`/templates/${id}/publish`).then((r) => r.data),
  archiveTemplate: (id) => api.post(`/templates/${id}/archive`).then((r) => r.data),
  cloneTemplate: (id) => api.post(`/templates/${id}/clone`).then((r) => r.data),
  addSection: (tid, payload) => api.post(`/templates/${tid}/sections`, payload).then((r) => r.data),
  removeSection: (tid, sid) => api.delete(`/templates/${tid}/sections/${sid}`).then((r) => r.data),
  generateSeats: (tid, payload) => api.post(`/templates/${tid}/seats/generate`, payload).then((r) => r.data),
  editSeat: (tid, sid, payload) => api.patch(`/templates/${tid}/seats/${sid}`, payload).then((r) => r.data),
  // Attach to event
  attachTemplate: (eventId, templateId) =>
    api.post(`/events/${eventId}/attach-template?template_id=${encodeURIComponent(templateId)}`).then((r) => r.data),
  detachTemplate: (eventId) => api.delete(`/events/${eventId}/attach-template`).then((r) => r.data),
  getEventSnapshot: (eventId) => api.get(`/events/${eventId}/venue-snapshot`).then((r) => r.data),
  // Event seats + admin holds
  listEventSeats: (eventId) => api.get(`/events/${eventId}/seats`).then((r) => r.data),
  holdSeats: (eventId, payload) => api.post(`/events/${eventId}/seats/hold`, payload).then((r) => r.data),
  releaseSeats: (eventId, payload) => api.post(`/events/${eventId}/seats/release`, payload).then((r) => r.data),
  // Public
  publicSeats: (slug) => api.get(`/public/events/${slug}/seats`).then((r) => r.data),
  reserveSeats: (slug, payload) => api.post(`/public/events/${slug}/seats/reserve`, payload).then((r) => r.data),
  bestTogether: (slug, payload) => api.post(`/public/events/${slug}/seats/best-together`, payload).then((r) => r.data),
  publicVenueLayout: (slug) => api.get(`/public/events/${slug}/venue-layout`).then((r) => r.data),
  publicSectionAvailability: (slug) => api.get(`/public/events/${slug}/section-availability`).then((r) => r.data),
  // Sprint G — organiser layout designer
  getLayout: (tid) => api.get(`/templates/${tid}/layout`).then((r) => r.data),
  saveLayout: (tid, payload) => api.put(`/templates/${tid}/layout`, payload).then((r) => r.data),
  previewLayout: (payload) => api.post(`/layouts/preview`, payload).then((r) => r.data),
  // Sprint K — pricing zones + section geometry
  listPricingZones: (tid) => api.get(`/templates/${tid}/pricing-zones`).then((r) => r.data),
  createPricingZone: (tid, payload) => api.post(`/templates/${tid}/pricing-zones`, payload).then((r) => r.data),
  updatePricingZone: (tid, zid, payload) => api.patch(`/templates/${tid}/pricing-zones/${zid}`, payload).then((r) => r.data),
  deletePricingZone: (tid, zid) => api.delete(`/templates/${tid}/pricing-zones/${zid}`).then((r) => r.data),
  updateSection: (tid, sid, payload) => api.patch(`/templates/${tid}/sections/${sid}`, payload).then((r) => r.data),
  bulkUpdateSections: (tid, items) => api.post(`/templates/${tid}/sections/bulk-update`, { items }).then((r) => r.data),
  regenerateSectionSeats: (tid, sid, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.post(`/templates/${tid}/sections/${sid}/regenerate${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  // Sprint M — buyer preview from live template data
  previewAsBuyer: (tid) => api.get(`/templates/${tid}/preview-as-buyer`).then((r) => r.data),
};

export const VENUE_TYPES = [
  { value: "hall", label: "Hall" },
  { value: "theatre", label: "Theatre" },
  { value: "stadium", label: "Stadium" },
  { value: "outdoor", label: "Outdoor" },
  { value: "school", label: "School" },
  { value: "church", label: "Church" },
  { value: "community_centre", label: "Community Centre" },
  { value: "conference_centre", label: "Conference Centre" },
  { value: "other", label: "Other" },
];

export const TEMPLATE_TYPES = [
  { value: "general_admission", label: "General Admission" },
  { value: "reserved_seating", label: "Reserved Seating" },
  { value: "table_seating", label: "Table Seating" },
];

export const SEAT_HOLD_REASONS = [
  "vip","sponsor","staff","performer","media","camera","organiser","accessible","other",
];

// Sprint B — event categories. Keep in sync with backend EVENT_CATEGORIES.
export const EVENT_CATEGORIES = [
  { value: "music", label: "Music" },
  { value: "comedy", label: "Comedy" },
  { value: "food_and_wine", label: "Food & Wine" },
  { value: "workshop", label: "Workshop" },
  { value: "sport", label: "Sport" },
  { value: "community", label: "Community" },
  { value: "business", label: "Business" },
  { value: "family", label: "Family" },
  { value: "charity", label: "Charity" },
  { value: "other", label: "Other" },
];

export function categoryLabel(value) {
  const m = EVENT_CATEGORIES.find((c) => c.value === value);
  return m ? m.label : null;
}

export const checkoutApi = {
  create: (payload) => api.post("/public/checkout", payload).then((r) => r.data),
  get: (id) => api.get(`/public/checkout/${id}`).then((r) => r.data),
  status: (id) => api.get(`/public/checkout/${id}/status`).then((r) => r.data),
  complete: (id, payload) => api.post(`/public/checkout/${id}/complete`, payload).then((r) => r.data),
  cancel: (id) => api.post(`/public/checkout/${id}/cancel`).then((r) => r.data),
  getOrder: (orderId, token) => api.get(`/public/orders/${orderId}?token=${encodeURIComponent(token || "")}`).then((r) => r.data),
};

// Sprint AY — Order Confirmation Hub
export const ordersApi = {
  get: (orderId, token) => api.get(`/public/orders/${orderId}?token=${encodeURIComponent(token || "")}`).then((r) => r.data),
  resend: (orderId, token) => api.post(`/public/orders/${orderId}/resend?token=${encodeURIComponent(token || "")}`).then((r) => r.data),
};

export const downloadAllTicketsUrl = (orderId, token) =>
  `${BACKEND_URL}/api/public/orders/${orderId}/tickets.pdf?token=${encodeURIComponent(token || "")}`;

// Sprint AY — Wallet feature flags (Apple/Google)
export const walletApi = {
  config: () => api.get(`/public/wallet/config`).then((r) => r.data),
  applePassUrl: (ticketCode, orderToken) =>
    `${BACKEND_URL}/api/public/tickets/${ticketCode}/apple-wallet?order_token=${encodeURIComponent(orderToken || "")}`,
  googlePassUrl: (ticketCode, orderToken) =>
    `${BACKEND_URL}/api/public/tickets/${ticketCode}/google-wallet?order_token=${encodeURIComponent(orderToken || "")}`,
};

export const ticketsApi = {
  get: (code) => api.get(`/public/tickets/${code}`).then((r) => r.data),
  resend: (email) => api.post(`/public/tickets/resend`, { email }).then((r) => r.data),
};

// Sprint AK — Ticket Token Numbers
export const tokensApi = {
  patch: (ticketId, tokenNumber) =>
    api.patch(`/tickets/${ticketId}/token`, { token_number: tokenNumber }).then((r) => r.data),
  regenerate: (ticketId) =>
    api.post(`/tickets/${ticketId}/token/regenerate`).then((r) => r.data),
  backfill: (eventId, opts = {}) =>
    api
      .post(`/events/${eventId}/tickets/backfill-tokens`, {
        dry_run: !!opts.dry_run,
        include_refunded: opts.include_refunded !== false,
      })
      .then((r) => r.data),
};

export const scannerTokensApi = {
  list: (eventId) => api.get(`/events/${eventId}/scanner-tokens`).then((r) => r.data),
  create: (eventId, payload = {}) =>
    api.post(`/events/${eventId}/scanner-tokens`, payload).then((r) => r.data),
  revoke: (eventId, tokenId) =>
    api.post(`/events/${eventId}/scanner-tokens/${tokenId}/revoke`).then((r) => r.data),
};

export const reportsApi = {
  summary: (eventId) => api.get(`/events/${eventId}/reports/summary`).then((r) => r.data),
  orders: (eventId) => api.get(`/events/${eventId}/reports/orders`).then((r) => r.data),
  attendees: (eventId) => api.get(`/events/${eventId}/reports/attendees`).then((r) => r.data),
  editAttendee: (eventId, ticketId, payload) =>
    api.post(`/events/${eventId}/tickets/${ticketId}/attendee`, payload).then((r) => r.data),
  resendTicket: (eventId, ticketId) =>
    api.post(`/events/${eventId}/tickets/${ticketId}/resend`).then((r) => r.data),
  resendOrderConfirmation: (eventId, orderId) =>
    api.post(`/events/${eventId}/reports/orders/${orderId}/resend-confirmation`).then((r) => r.data),
  piiAccessLog: (eventId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== "")
    ).toString();
    return api.get(`/events/${eventId}/pii-access-log${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  piiAccessLogCsv: (eventId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== "")
    ).toString();
    return api
      .get(`/events/${eventId}/pii-access-log.csv${qs ? `?${qs}` : ""}`, { responseType: "blob" })
      .then((r) => ({ blob: r.data, headers: r.headers }));
  },
  setPiiPolicy: (eventId, hideTeamPii) =>
    api.patch(`/events/${eventId}/pii-policy`, { hide_team_pii: hideTeamPii }).then((r) => r.data),
  ticketHistory: (eventId, ticketId) =>
    api.get(`/events/${eventId}/tickets/${ticketId}/history`).then((r) => r.data),
  bulkFixPreview: (eventId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/events/${eventId}/attendees/bulk-fix/preview`, fd).then((r) => r.data);
  },
  bulkFixApply: (eventId, rows, resend) =>
    api.post(`/events/${eventId}/attendees/bulk-fix/apply`, { rows, resend }).then((r) => r.data),
};

// Sprint AZ — Staff order management (admin + authorised organiser)
export const staffOrdersApi = {
  get: (orderId) => api.get(`/orders/${orderId}`).then((r) => r.data),
  resend: (orderId) => api.post(`/orders/${orderId}/resend`).then((r) => r.data),
  adminList: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== "")
    ).toString();
    return api.get(`/admin/orders${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
};

export const staffTicketPdfUrl = (orderId) =>
  `${BACKEND_URL}/api/orders/${orderId}/tickets.pdf`;
export const staffSingleTicketPdfUrl = (orderId, ticketId) =>
  `${BACKEND_URL}/api/orders/${orderId}/tickets/${ticketId}/pdf`;

// Sprint AI — Organiser dashboard aggregates (Sales + Payouts)
export const organiserDashboardApi = {
  salesSummary: (range = "30d") =>
    api.get(`/organiser/sales/summary?range=${encodeURIComponent(range)}`).then((r) => r.data),
  payoutsSummary: () => api.get(`/organiser/payouts/summary`).then((r) => r.data),
};

export const refundsApi = {
  refundOrder: (orderId, payload) => api.post(`/orders/${orderId}/refund`, payload).then((r) => r.data),
  listOrderRefunds: (orderId) => api.get(`/orders/${orderId}/refunds`).then((r) => r.data),
  voidTicket: (ticketId, payload = {}) => api.post(`/tickets/${ticketId}/void`, payload).then((r) => r.data),
  cancelEvent: (eventId, payload = {}) => api.post(`/events/${eventId}/cancel`, payload).then((r) => r.data),
};

export const adminApi = {
  overview: () => api.get(`/admin/overview`).then((r) => r.data),
  accounts: (q = "", opts = {}) => {
    const parts = [];
    if (q) parts.push(`q=${encodeURIComponent(q)}`);
    if (opts.eligibleForEvent) parts.push(`eligible_for_event=${encodeURIComponent(opts.eligibleForEvent)}`);
    return api.get(`/admin/accounts${parts.length ? `?${parts.join("&")}` : ""}`).then((r) => r.data);
  },
  getAccount: (id) => api.get(`/admin/accounts/${id}`).then((r) => r.data),
  createAccount: (payload) => api.post(`/admin/accounts`, payload).then((r) => r.data),
  updateAccount: (id, payload) => api.patch(`/admin/accounts/${id}`, payload).then((r) => r.data),
  updateFeeSettings: (id, payload) => api.patch(`/admin/accounts/${id}/fee-settings`, payload).then((r) => r.data),
  // Sprint AO — per-event fees
  getEventFees: (eventId) => api.get(`/admin/events/${eventId}/fees`).then((r) => r.data),
  updateEventPaymentSettings: (eventId, payload) =>
    api.patch(`/admin/events/${eventId}/payment-settings`, payload).then((r) => r.data),
  updateTicketPrice: (eventId, ttId, priceCents) =>
    api.patch(`/admin/events/${eventId}/ticket-types/${ttId}/price`, { price_cents: priceCents }).then((r) => r.data),
  sendInvite: (id) => api.post(`/admin/accounts/${id}/send-invite`).then((r) => r.data),
  sendPasswordReset: (id) => api.post(`/admin/accounts/${id}/password-reset`).then((r) => r.data),
  suspend: (id, reason) => api.post(`/admin/accounts/${id}/suspend`, { reason }).then((r) => r.data),
  unsuspend: (id) => api.post(`/admin/accounts/${id}/unsuspend`).then((r) => r.data),
  events: (q = "", ownerId = "") => {
    const parts = [];
    if (q) parts.push(`q=${encodeURIComponent(q)}`);
    if (ownerId) parts.push(`owner_id=${encodeURIComponent(ownerId)}`);
    const qs = parts.length ? `?${parts.join("&")}` : "";
    return api.get(`/admin/events${qs}`).then((r) => r.data);
  },
  createEventForOrganiser: (payload) => api.post(`/admin/events`, payload).then((r) => r.data),
  // Sprint AW — canonical event ownership transfer (admin-only)
  transferEventOwner: (eventId, payload) =>
    api.patch(`/admin/events/${eventId}/owner`, payload).then((r) => r.data),
  // Sprint AW UX fix — copy invite link (team-admin scoped, pending only)
  getInviteLink: (eventId, memberId) =>
    api.get(`/events/${eventId}/team/${memberId}/invite-link`).then((r) => r.data),
  unpublishEvent: (id, reason) => api.post(`/admin/events/${id}/unpublish`, { reason }).then((r) => r.data),
  getEventPolicies: (id) => api.get(`/admin/events/${id}/policies`).then((r) => r.data),
  updateEventPolicies: (id, payload) => api.patch(`/admin/events/${id}/policies`, payload).then((r) => r.data),
  auditLogs: (params = {}) => api.get(`/admin/audit-logs`, { params }).then((r) => r.data),
  getConfig: () => api.get(`/admin/config`).then((r) => r.data),
  patchConfig: (payload) => api.patch(`/admin/config`, payload).then((r) => r.data),
  // Sprint BE + BF — admin cross-organiser ticket management (canonical paginated).
  searchOrders: (params = {}) => api.get(`/admin/orders/search`, { params }).then((r) => r.data),
  searchTickets: (params = {}) => api.get(`/admin/tickets/search`, { params }).then((r) => r.data),
  getTicket: (ticketId) => api.get(`/admin/tickets/${ticketId}`).then((r) => r.data),
  getOrder: (orderId) => api.get(`/admin/orders/${orderId}`).then((r) => r.data),
  resendOrderTickets: (orderId, payload) =>
    api.post(`/admin/orders/${orderId}/tickets/resend`, payload).then((r) => r.data),
  resendSingleTicket: (ticketId, payload) =>
    api.post(`/admin/tickets/${ticketId}/resend`, payload).then((r) => r.data),
  // Single ticket PDF download (historical=true returns a watermarked copy for
  // voided/cancelled/refunded tickets).
  downloadTicketPdf: (ticketId, historical = false) =>
    api.get(`/admin/tickets/${ticketId}/pdf`, { params: { historical }, responseType: "blob" })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // Sprint BF — selected tickets ZIP download (POST because body carries ticket_ids).
  downloadSelectedTickets: (orderId, ticketIds, historical = false) =>
    api.post(
      `/admin/orders/${orderId}/tickets/download-selected`,
      { ticket_ids: ticketIds, historical },
      { responseType: "blob" },
    ).then((r) => ({ blob: r.data, headers: r.headers })),
  // Note: `orders` list is now served by staffOrdersApi.adminList (Sprint AZ) —
  // do not add it back here.
  // Admin-only manual attendee / ticket issuing, replacement & manual refunds
  manualOrganisers: (q = "") => api.get(`/admin/manual/organisers`, { params: { q } }).then((r) => r.data),
  manualEvents: (organiserId) => api.get(`/admin/manual/events`, { params: { organiser_id: organiserId } }).then((r) => r.data),
  manualTicketTypes: (eventId) => api.get(`/admin/manual/ticket-types`, { params: { event_id: eventId } }).then((r) => r.data),
  manualPreview: (payload) => api.post(`/admin/manual/preview`, payload).then((r) => r.data),
  manualIssue: (payload) => api.post(`/admin/manual/issue`, payload).then((r) => r.data),
  manualReplace: (payload) => api.post(`/admin/manual/replace`, payload).then((r) => r.data),
  manualRefund: (orderId, payload) => api.post(`/admin/manual/orders/${orderId}/refund`, payload).then((r) => r.data),
};

// Platform Super Admin — cross-organiser ticket & attendee management
export const superAdminApi = {
  organisers: (q = "") => api.get(`/admin/super/organisers`, { params: { q } }).then((r) => r.data),
  events: (organiserId, params = {}) =>
    api.get(`/admin/super/organisers/${organiserId}/events`, { params }).then((r) => r.data),
  overview: (eventId) => api.get(`/admin/super/events/${eventId}/overview`).then((r) => r.data),
  bookings: (eventId, params = {}) =>
    api.get(`/admin/super/events/${eventId}/bookings`, { params }).then((r) => r.data),
  tickets: (eventId, params = {}) =>
    api.get(`/admin/super/events/${eventId}/tickets`, { params }).then((r) => r.data),
  booking: (orderId) => api.get(`/admin/super/orders/${orderId}`).then((r) => r.data),
  createBooking: (payload) => api.post(`/admin/super/create-booking`, payload).then((r) => r.data),
  editTicket: (ticketId, payload) => api.post(`/admin/super/tickets/${ticketId}/edit`, payload).then((r) => r.data),
  checkin: (ticketId, payload = {}) => api.post(`/admin/super/tickets/${ticketId}/checkin`, payload).then((r) => r.data),
  undoCheckin: (ticketId, payload = {}) => api.post(`/admin/super/tickets/${ticketId}/undo-checkin`, payload).then((r) => r.data),
  ticketDeletePreview: (ticketId) => api.get(`/admin/super/tickets/${ticketId}/delete-preview`).then((r) => r.data),
  orderDeletePreview: (orderId) => api.get(`/admin/super/orders/${orderId}/delete-preview`).then((r) => r.data),
  deleteTicket: (ticketId, payload) => api.post(`/admin/super/tickets/${ticketId}/delete`, payload).then((r) => r.data),
  deleteBooking: (orderId, payload) => api.post(`/admin/super/orders/${orderId}/delete`, payload).then((r) => r.data),
  purgeTicket: (ticketId, payload) => api.post(`/admin/super/tickets/${ticketId}/purge`, payload).then((r) => r.data),
  reconcileRefund: (refundId) => api.post(`/admin/super/refunds/${refundId}/reconcile`).then((r) => r.data),
};

export function adminTicketPdfUrl(ticketId, opts = {}) {
  const qs = opts.historical ? "?historical=true" : "";
  return `${BACKEND_URL}/api/admin/tickets/${ticketId}/pdf${qs}`;
}

export function adminOrderTicketsZipUrl(orderId) {
  return `${BACKEND_URL}/api/admin/orders/${orderId}/tickets.zip`;
}

// Sprint T1 — event add-ons (Extras)
export const addonsApi = {
  list: (eventId) => api.get(`/events/${eventId}/addons`).then((r) => r.data),
  create: (eventId, payload) => api.post(`/events/${eventId}/addons`, payload).then((r) => r.data),
  update: (addonId, payload) => api.patch(`/addons/${addonId}`, payload).then((r) => r.data),
  archive: (addonId) => api.post(`/addons/${addonId}/archive`).then((r) => r.data),
  publicList: (slug) => api.get(`/public/events/${slug}/addons`).then((r) => r.data),
};

export function ordersCsvUrl(eventId) {
  return `${BACKEND_URL}/api/events/${eventId}/reports/orders.csv`;
}

export function attendeesCsvUrl(eventId) {
  return `${BACKEND_URL}/api/events/${eventId}/reports/attendees.csv`;
}

export function attendeesXlsxUrl(eventId) {
  return `${BACKEND_URL}/api/events/${eventId}/reports/attendees.xlsx`;
}

// Sprint AI follow-up — Sales aggregate CSV for the organiser (own events only).
export function organiserSalesCsvUrl(range = "30d") {
  return `${BACKEND_URL}/api/organiser/sales/summary.csv?range=${encodeURIComponent(range)}`;
}

// Scanner APIs — no auth; authorised via X-Scanner-Token header.
export function scanApi(scannerToken) {
  const headers = { "X-Scanner-Token": scannerToken };
  return {
    session: () => api.get("/scan/session", { headers }).then((r) => r.data),
    validate: (payload) => api.post("/scan/validate", payload, { headers }).then((r) => r.data),
    manual: (payload) => api.post("/scan/manual-checkin", payload, { headers }).then((r) => r.data),
    sync: (payload) => api.post("/scan/sync", payload, { headers }).then((r) => r.data),
    attendees: (q = "") =>
      api.get(`/scan/attendees${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers }).then((r) => r.data),
    stats: () => api.get("/scan/stats", { headers }).then((r) => r.data),
    logs: (limit = 50) => api.get(`/scan/logs?limit=${limit}`, { headers }).then((r) => r.data),
    revocations: (since = 0) =>
      api.get(`/scan/revocations?since=${since}`, { headers }).then((r) => r.data),
  };
}

export function ticketPdfUrl(code) {
  return `${BACKEND_URL}/api/public/tickets/${code}/pdf`;
}

export function emailPreviewUrl(logId) {
  return `${BACKEND_URL}/api/public/emails/${logId}/preview`;
}

export function fileUrl(fileId) {
  if (!fileId) return null;
  return `${BACKEND_URL}/api/files/${fileId}`;
}

// Resolves an event's cover image. Prefers uploaded file, then external URL,
// then null so the caller can render its own placeholder.
export function coverImageUrl(event) {
  if (!event) return null;
  if (event.cover_file_id) return `${BACKEND_URL}/api/files/${event.cover_file_id}`;
  if (event.cover_url) return event.cover_url;
  return null;
}

// AUD helpers
export function centsToAUD(cents) {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export function audToCents(input) {
  if (input === "" || input == null) return 0;
  const n = parseFloat(String(input).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

// Sprint AU — Australian state → IANA timezone mapping (used to auto-suggest event timezone).
export const STATE_TZ_MAP = {
  NSW: "Australia/Sydney",
  ACT: "Australia/Sydney",
  VIC: "Australia/Melbourne",
  QLD: "Australia/Brisbane",
  TAS: "Australia/Hobart",
  SA: "Australia/Adelaide",
  WA: "Australia/Perth",
  NT: "Australia/Darwin",
};

// Buyer-friendly labels for the technical IANA timezone we display next to event times.
export const TZ_FRIENDLY_LABEL = {
  "Australia/Sydney": "New South Wales / ACT local time",
  "Australia/Melbourne": "Victoria local time",
  "Australia/Brisbane": "Queensland local time",
  "Australia/Hobart": "Tasmania local time",
  "Australia/Adelaide": "South Australia local time",
  "Australia/Perth": "Western Australia local time",
  "Australia/Darwin": "Northern Territory local time",
};

export function tzForState(state) {
  return STATE_TZ_MAP[(state || "").toUpperCase()] || "Australia/Sydney";
}

export function tzFriendlyLabel(tz) {
  return TZ_FRIENDLY_LABEL[tz] || tz || "";
}

// DD/MM/YYYY formatter for AU. Sprint AU — accept optional IANA tz (falls back to Sydney).
export function formatAuDate(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz || "Australia/Sydney",
  });
}

// Convert an ISO datetime to the value that <input type="datetime-local"> expects
export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Use user's local timezone for the input, but store UTC ISO on submit
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Sprint AP — event team management
export const teamApi = {
  meta: () => api.get("/team/meta").then((r) => r.data),
  list: (eventId) => api.get(`/events/${eventId}/team`).then((r) => r.data),
  invite: (eventId, payload) => api.post(`/events/${eventId}/team`, payload).then((r) => r.data),
  update: (eventId, memberId, payload) =>
    api.patch(`/events/${eventId}/team/${memberId}`, payload).then((r) => r.data),
  resend: (eventId, memberId) => api.post(`/events/${eventId}/team/${memberId}/resend`).then((r) => r.data),
  remove: (eventId, memberId) => api.delete(`/events/${eventId}/team/${memberId}`).then((r) => r.data),
  activity: (eventId, memberId, params) =>
    api.get(`/events/${eventId}/team/${memberId}/activity`, { params }).then((r) => r.data),
  inviteInfo: (token) => api.get(`/auth/team-invite/${token}`).then((r) => r.data),
  acceptInvite: (payload) => api.post("/auth/team-invite/accept", payload).then((r) => r.data),
};
